import { useEffect, useMemo } from "react";
import { FinaOpenBankingConsentStatus, PayoutInformationConsentStatus } from "Shared/util/types/api";
import { getPayoutInformationConsentStatus } from "Shared/util/web-api";
import { usePayoutInformationContext } from "../../context";
import { useLocalNotificationBanner, useTranslation } from "Shared/util/custom-hooks";

export const useConsentPoller = (onFirstConsentImport: () => void): void => {
    const {
        values: { bankConsents },
    } = usePayoutInformationContext();

  const t = useTranslation(
    (x) => x.edit_payment_information.messageCodes
  );

  const {
    NotificationBannerType,
    addLocalNotificationBanner,
  } = useLocalNotificationBanner();

    const allConsentIdsInProgress: number[] = useMemo(
        () =>
            (bankConsents || []).reduce<number[]>(
                (output, item) => output.concat(item.inProgressConsentIds),
                [],
            ),
        [bankConsents],
    );

    useEffect(() => {
        const timeouts: NodeJS.Timeout[] = [];

        const hasNothingToPoll = !allConsentIdsInProgress?.length;
        if (hasNothingToPoll) {
            return;
        }

        const createConsentPollingPromise = (
            consentId: number,
        ): Promise<void> =>
            new Promise((resolve, reject) => {
                const poll = () => {
                    getPayoutInformationConsentStatus(consentId)
                        .then((consent) => {
                            if (
                                consent.status !==
                                FinaOpenBankingConsentStatus.Created
                            ) {
                                checkForErrorMessages(consent);
                                resolve();
                            } else {
                                const timeout = setTimeout(poll, 5000);
                                timeouts.push(timeout);
                            }
                        })
                        .catch((error) => {
                            console.error(
                                `Polling failed for consentId ${consentId}:`,
                                error,
                            );
                            reject(error);
                        });
                };

                poll();
            });

        const pollingRequests = allConsentIdsInProgress.map((consentId) =>
            createConsentPollingPromise(consentId),
        );

        Promise.any(pollingRequests)
            .then(() => {
                onFirstConsentImport();
            })
            .catch((error) => {
                console.error("Every Fina-consent-status-poll failed", error);
            });

        return () => {
            timeouts.forEach((timeout) => clearTimeout(timeout));
        };
    }, [allConsentIdsInProgress, onFirstConsentImport]);

    const checkForErrorMessages = (consent: PayoutInformationConsentStatus) => {
        if (consent.status === FinaOpenBankingConsentStatus.Invalid){
            consent.externalErrorMessages.map((x) => {
                addLocalNotificationBanner(
                    t.findByKey(x) ? t.findByKey(x) : t.UNDEFINED,
                    NotificationBannerType.Error);
            })
        }
    }
};
